import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import ArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeftRounded";
import ArrowRightIcon from "@material-ui/icons/KeyboardArrowRightRounded";
import clsx from "clsx";
import React, { PropsWithRef, useMemo } from "react";
import { IconButton } from "../../interaction";
import { Color, sidePanelWidthCollapsed, sidePanelWidthExpanded, spinHeaderHeight } from "../../theme";
import { HasTestId, PlainFunctionComponent } from "../../types";
import { ifDefined, isDefined } from "../../util";
import { useBurgerMenuElement } from "./useBurgerMenuElement";

export type BurgerableMenuProps = PropsWithRef<{ onBurgerMenuClose: () => void }>;
export interface PermanentMenuProps {
    expanded: boolean;
}

export interface NavigationDrawerProps extends HasTestId {
    /**
     * Will be shown before `BurgerableMenu` and is only visible on `expanded` state.
     */
    beforeBurgerableMenu?: React.ReactNode;
    BurgerableMenu?: React.ElementType<BurgerableMenuProps>;
    expanded: boolean;
    onToggleExpanded: () => void;
    PermanentMenu?: React.ElementType<PermanentMenuProps>;
    title: React.ReactNode;
}

export const NavigationDrawer: PlainFunctionComponent<NavigationDrawerProps> = ({
    beforeBurgerableMenu,
    BurgerableMenu,
    expanded,
    onToggleExpanded,
    PermanentMenu,
    testId,
    title,
}) => {
    const [onBurgerMenuClose, wrapIntoBurgerMenu] = useBurgerMenuElement();

    const burgerableMenu = useMemo(() => ifDefined(BurgerableMenu, (Menu) => <Menu key="burgerable-menu" onBurgerMenuClose={onBurgerMenuClose} />), [
        BurgerableMenu,
        onBurgerMenuClose,
    ]);

    const classes = useStyles();
    // Note: `class` must be set on both `Drawer` (= `root`) and `Paper` in order to 1) align right components (`Drawer`) and 2) displayed content (`Paper`)
    const drawerAndPaperClassName = expanded ? classes.drawerExpanded : classes.drawerCollapsed;
    return (
        <Drawer anchor="left" classes={{ root: drawerAndPaperClassName, paper: clsx(classes.drawer, drawerAndPaperClassName) }} variant="permanent" data-testid={testId}>
            <Box display="flex" flexDirection="column" height="100%" width="100%">
                <Box key="title-area" className={classes.drawerTitleArea}>
                    <Box key="drawer-title" className={classes.drawerTitle}>
                        {expanded && title}
                    </Box>
                </Box>
                {expanded && beforeBurgerableMenu}
                <Box className={classes.drawerListArea}>
                    {ifDefined(burgerableMenu, (menu) => (
                        <List key="burgerable-menu" component="nav" className={classes.drawerList}>
                            {expanded ? menu : wrapIntoBurgerMenu(menu)}
                        </List>
                    ))}
                    {isDefined(BurgerableMenu) && isDefined(PermanentMenu) && <Divider key="overline-divider" />}
                    {ifDefined(PermanentMenu, (Menu) => (
                        <List key="collapsible-menu" component="nav" className={classes.drawerList}>
                            <Menu expanded={expanded} />
                        </List>
                    ))}
                </Box>
                {/* Alignment at bottom */}
                <Box key="bottom-box" className={classes.drawerBottomBox}>
                    <Divider />
                    <Box className={clsx(classes.drawerBottomIconBox, { [classes.drawerBottomIconBoxExpanded]: expanded })}>
                        <IconButton onClick={onToggleExpanded}>{expanded ? <ArrowLeftIcon key="collapse-button" /> : <ArrowRightIcon key="expand-button" />}</IconButton>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawer: {
            flexShrink: 0,
            fontSize: 12,
            lineHeight: "1em",
            overflowX: "hidden",
            whiteSpace: "nowrap",

            // Move drawer down by the height of the SupplyOn spin header
            marginTop: spinHeaderHeight,
            paddingBottom: spinHeaderHeight,
        },
        drawerCollapsed: {
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: sidePanelWidthCollapsed,
        },
        drawerExpanded: {
            transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
            }),
            width: sidePanelWidthExpanded,
        },
        drawerTitleArea: {
            alignItems: "flex-end",
            backgroundColor: Color.blue2,
            color: Color.white,
            display: "flex",
            flexShrink: 0,
            height: 144,
        },
        drawerTitle: {
            fontSize: 16,
            fontWeight: "bold",
            lineHeight: "1em",
            margin: 24,
            overflow: "hidden",
            width: 240,
        },
        drawerListArea: {
            flexGrow: 3,
            padding: "8px 0", // plus 8px is of `MuiList`s padding
            overflowX: "hidden",
            overflowY: "auto",
        },
        drawerList: {
            margin: "8px 0", // plus 8px is of `MuiList`s padding
        },
        drawerBottomBox: {
            height: 48,
            minHeight: 48,
            width: "100%",
        },
        drawerBottomIconBox: {
            display: "flex",
            justifyContent: "flex-end",
            padding: 8,
        },
        drawerBottomIconBoxExpanded: {
            marginRight: 6, // additional `6px` from the left border in order to get `16px` distance from the border instead of `8px` in collapsed status
        },
    }),
);
